/**
 * The 2D stand-in's triangles, collected into one buffer and drawn in as few
 * calls as they allow: a batch holds one kind of triangle with one blend and
 * one texture, and is drawn as soon as the next triangle needs another, or a
 * heavy layer needs to draw over it.
 */
import type { BlendMode, Gpu, Viewport } from './gpu'

/** Strokes and fills; circles and ellipses; images. Each is drawn by its own program. */
export type Kind = 'path' | 'disc' | 'sprite'

/** Floats per vertex, for each kind. */
const STRIDE: Record<Kind, number> = { path: 8, disc: 30, sprite: 8 }

export class Batch {
  /** The vertices, `n` floats of them so far. */
  f = new Float32Array(1 << 16)
  n = 0

  private kind: Kind | null = null
  private blend: BlendMode | null = null
  private texture: WebGLTexture | null = null
  private viewport: Viewport = { width: 0, height: 0, dpr: 1 }
  private gpu: Gpu | null = null
  private buffer: WebGLBuffer | null = null
  private sprites = new WeakMap<object, WebGLTexture>()

  constructor(
    /** Whether the GPU can draw; batches are dropped while it cannot. */
    private readonly ready: () => boolean
  ) {}

  /** Builds its GPU resources on this context: once, and again after it is restored. */
  rebuild(gpu: Gpu) {
    this.gpu = gpu
    this.sprites = new WeakMap()
    this.buffer = gpu.gl.createBuffer()
  }

  /** Starts a frame, with nothing batched. */
  begin(viewport: Viewport) {
    this.viewport = viewport
    this.n = 0
  }

  /** Space for `floats` more of this kind, drawing what is batched first if it is another kind or full. */
  room(kind: Kind, blend: BlendMode, texture: WebGLTexture | null, floats: number) {
    if (this.kind !== kind || this.blend !== blend || this.texture !== texture) {
      this.flush()
      this.kind = kind
      this.blend = blend
      this.texture = texture
    }
    if (this.n + floats > this.f.length) {
      if (this.n) {
        this.flush()
        this.kind = kind
        this.blend = blend
        this.texture = texture
      }
      if (floats > this.f.length) this.f = new Float32Array(1 << Math.ceil(Math.log2(floats)))
    }
    return this.f
  }

  /** Draws whatever is batched. */
  flush() {
    if (!this.n || !this.kind || !this.ready()) {
      this.n = 0
      return
    }
    const gpu = this.gpu!
    const gl = gpu.gl
    const program = gpu.program(this.kind)
    const s = STRIDE[this.kind]
    const b = this.buffer!
    gpu.use(program, this.viewport)
    gpu.blend(this.blend!)
    gl.bindBuffer(gl.ARRAY_BUFFER, b)
    gl.bufferData(gl.ARRAY_BUFFER, this.f.subarray(0, this.n), gl.STREAM_DRAW)
    if (this.kind === 'path')
      gpu.bind(program, [
        ['aP', b, 2, s, 0],
        ['aC', b, 4, s, 2],
        ['aE', b, 2, s, 6],
      ])
    else if (this.kind === 'disc')
      gpu.bind(program, [
        ['aP', b, 2, s, 0],
        ['aCtr', b, 2, s, 2],
        ['aInv', b, 2, s, 4],
        ['aShape', b, 4, s, 6],
        ['aSp', b, 4, s, 10],
        ['aC0', b, 4, s, 14],
        ['aC1', b, 4, s, 18],
        ['aC2', b, 4, s, 22],
        ['aC3', b, 4, s, 26],
      ])
    else {
      gpu.bind(program, [
        ['aP', b, 2, s, 0],
        ['aUV', b, 2, s, 2],
        ['aC', b, 4, s, 4],
      ])
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, this.texture)
      gl.uniform1i(gpu.uniform(program, 'uTex'), 0)
    }
    gl.drawArrays(gl.TRIANGLES, 0, this.n / s)
    this.n = 0
  }

  /** An image's texture, uploaded the first time it is drawn. */
  sprite(image: CanvasImageSource) {
    let texture = this.sprites.get(image)
    if (texture) return texture
    this.flush()
    const gl = this.gpu!.gl
    texture = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image as TexImageSource)
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    this.sprites.set(image, texture)
    return texture
  }
}
